import React, {useCallback, useEffect, useRef, useState} from 'react';
import Webcam from 'react-webcam';
import {CanvasWrapper} from "./FaceDetection.style";

const API_URL = process.env.REACT_APP_WEB_SOCKET_URL || 'ws://localhost:8765';

const FaceDetection = ({
                           webcamRef, mirror = true, facingMode = "user", hide = false, onStatusChange = () => {
    }, onCapture
                       }) => {
    const [isConnected, setIsConnected] = useState(false);
    const [isCapturing, setIsCapturing] = useState(false);
    const [statusMessages, setStatusMessages] = useState([{text: 'Waiting for webcam access...', type: 'normal'}]);
    const [visualFeedback, setVisualFeedback] = useState(null);
    const [feedbackStatus, setFeedbackStatus] = useState('neutral'); // 'neutral', 'error', 'warning', 'success'
    // NEW: freeze flag - use ref only to avoid stale closure; removed state copy
    // const [isFrozen, setIsFrozen] = useState(false); // removed (was unused)
    const isFrozenRef = useRef(false); // keep latest frozen flag to avoid stale closure in socket callbacks

    // DEBUG states
    const [debugEnabled, setDebugEnabled] = useState(false);
    const [sentFrameResolution, setSentFrameResolution] = useState(null); // {width, height}
    const [lastServerMessageInterval, setLastServerMessageInterval] = useState(null); // ms
    const lastServerMessageTimeRef = useRef(null);

    const canvasRef = useRef(null);
    const hiddenCanvasRef = useRef(null); // For capturing clean image without drawings
    const socketRef = useRef(null);
    const videoContainerRef = useRef(null);
    const animationFrameRef = useRef(null);
    const lastSendTimeRef = useRef(0); // To track when the last WebSocket message was sent
    const facingModeRef = useRef(facingMode); // Track facingMode changes

    // CHANGED: Only target a fixed SHORT side of 360px; long side derived by aspect ratio.
    // We will not upscale if short side already <= 360.
    const TARGET_SHORT_SIDE = 360;
    const lastScaleRef = useRef(1); // scale factor applied when sending

    const WS_SEND_INTERVAL_MS = 200; // throttle interval for sending frames

    // Remove reliance on container aspect ratio; just track readiness
    const [videoReady, setVideoReady] = useState(false);

    // Track video dimensions for proper overlay scaling
    const [videoDimensions, setVideoDimensions] = useState({width: 0, height: 0});

    // Determine debug flag once on mount
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const dbg = params.get('debug');
            if (dbg && dbg.toLowerCase() === 'true') {
                setDebugEnabled(true);
            }
        } catch (e) {
            // ignore
        }
    }, []);

    const updateUI = useCallback((results) => {
        // Normalize incoming data to avoid undefined / string issues
        const faceCount = Number(results?.face_count) || 0;
        const alignmentIssue = results?.alignment_issue || null;
        const distanceIssue = results?.distance_issue || null;
        const lightingIssues = Array.isArray(results?.lighting_issues) ? results.lighting_issues.filter(Boolean) : [];

        const frozen = isFrozenRef.current;

        // If frozen but face lost (faceCount === 0) -> unfreeze immediately
        if (frozen && faceCount === 0) {
            console.debug('[FaceDetection] Face lost while frozen: unfreezing');
            isFrozenRef.current = false;
        }

        // If still frozen and still seeing face(s) keep success message (any faceCount > 0)
        if (isFrozenRef.current && faceCount > 0) {
            const frozenMsg = [{text: 'All checks passed. Hold still.', type: 'success'}];
            setStatusMessages(frozenMsg);
            setFeedbackStatus('success');
            onStatusChange({
                faceDetected: true,
                inPosition: true,
                goodLighting: true,
                statusMessages: frozenMsg,
                frozen: true
            });
            return; // skip further evaluation until face lost
        }

        let messages = [];
        let newStatus = 'success';

        if (faceCount > 0) {
            if (faceCount === 1) {
                messages.push({text: 'One face detected', type: 'success'});
            } else {
                messages.push({text: `${faceCount} faces detected`, type: 'error'});
                newStatus = 'error';
            }
        } else {
            messages.push({text: 'No Face detected', type: 'error'});
            newStatus = 'error';
        }

        const singleFace = faceCount === 1;
        const alignmentOK = singleFace && !alignmentIssue;
        const distanceOK = singleFace && !distanceIssue;
        const lightingOK = singleFace && lightingIssues.length === 0;

        if (singleFace) {
            if (alignmentIssue) {
                messages.push({text: alignmentIssue, type: 'error'});
                newStatus = 'error';
            } else {
                messages.push({text: 'Face aligned correctly', type: 'success'});
            }
            if (distanceIssue) {
                messages.push({text: distanceIssue, type: 'error'});
                newStatus = 'error';
            } else {
                messages.push({text: 'Face at correct distance', type: 'success'});
            }
            if (lightingIssues.length > 0) {
                messages.push({text: 'Lighting issues:', type: 'warning'});
                lightingIssues.forEach(issue => messages.push({text: `- ${issue}`, type: 'warning'}));
                if (newStatus === 'success') newStatus = 'warning';
            } else {
                messages.push({text: 'Lighting conditions good', type: 'success'});
            }
        }

        // Freeze only if everything green
        if (singleFace && alignmentOK && distanceOK && lightingOK && newStatus === 'success') {
            const frozenMsg = [{text: 'All checks passed. Hold still.', type: 'success'}];
            isFrozenRef.current = true;
            console.debug('[FaceDetection] All green -> freezing');
            setStatusMessages(frozenMsg);
            setFeedbackStatus('success');
            onStatusChange({
                faceDetected: true,
                inPosition: true,
                goodLighting: true,
                statusMessages: frozenMsg,
                frozen: true
            });
            return;
        }

        setStatusMessages(messages);
        setFeedbackStatus(newStatus);
        onStatusChange({
            faceDetected: faceCount > 0,
            inPosition: alignmentOK && distanceOK,
            goodLighting: lightingOK,
            statusMessages: messages,
            frozen: false
        });
    }, [onStatusChange]);

    const connectWebSocket = useCallback(() => {
        console.log("WEBSOCKET", API_URL);
        const socket = new WebSocket(API_URL);

        socket.onopen = () => {
            console.log('WebSocket connected');
            setIsConnected(true);
        };

        socket.onmessage = (event) => {
            const now = Date.now();
            if (lastServerMessageTimeRef.current) {
                setLastServerMessageInterval(now - lastServerMessageTimeRef.current);
            }
            lastServerMessageTimeRef.current = now;

            const results = JSON.parse(event.data);
            // console.log('Results:', results);

            // Extract visual feedback if available
            if (results.visual_feedback) {
                setVisualFeedback(results.visual_feedback);
            } else {
                setVisualFeedback(null);
            }

            updateUI(results);
        };

        socket.onclose = () => {
            console.log('WebSocket disconnected');
            setIsConnected(false);

            setTimeout(connectWebSocket, 2000);
        };

        socket.onerror = (error) => {
            console.error('WebSocket error:', error);
            socket.close();
        };

        socketRef.current = socket;
    }, [updateUI]);

    // OPTIMIZED: Only draw overlay (ellipse and darkened area), not the video feed
    const drawGuide = useCallback(() => {
        if (!canvasRef.current || !videoDimensions.width || !videoDimensions.height) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d', {alpha: true});

        const width = canvas.width;
        const height = canvas.height;

        // Clear the canvas completely (transparent)
        ctx.clearRect(0, 0, width, height);

        // Default oval guide (used when no feedback)
        let centerX = width / 2;
        let centerY = height / 2;
        const ovalAspectRatio = 0.75; // width:height ratio

        // Calculate the maximum possible size for the oval that fits within the canvas
        let radiusY, radiusX;

        // First, try to fit based on height constraint
        radiusY = height * 0.4; // Use 40% of height
        radiusX = radiusY * ovalAspectRatio;

        // If too wide, recalculate based on width constraint
        if (radiusX * 2 > width * 0.8) {
            radiusX = width * 0.4; // Use 40% of width
            radiusY = radiusX / ovalAspectRatio;
        }

        // If we have visual feedback with ellipse data, use that instead
        if (visualFeedback && visualFeedback.ellipse) {
            const scaleFactor = 1 / (lastScaleRef.current || 1);

            const feedbackEllipse = visualFeedback.ellipse;
            centerX = feedbackEllipse.center[0] * scaleFactor;
            centerY = feedbackEllipse.center[1] * scaleFactor;
            radiusX = feedbackEllipse.axes_lengths[0] * scaleFactor;
            radiusY = feedbackEllipse.axes_lengths[1] * scaleFactor;
        }

        // Draw darkened overlay everywhere EXCEPT the oval
        ctx.save();

        // Fill the entire canvas with dark overlay
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.fillRect(0, 0, width, height);

        // Cut out the oval area using destination-out composite mode
        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
        ctx.fill();

        // Reset composite mode
        ctx.globalCompositeOperation = 'source-over';
        ctx.restore();

        // Draw oval outline with color based on status
        ctx.beginPath();
        ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);

        // Set color based on feedback status
        if (feedbackStatus === 'error') {
            ctx.strokeStyle = 'red';
        } else if (feedbackStatus === 'warning') {
            ctx.strokeStyle = 'yellow';
        } else if (feedbackStatus === 'success') {
            ctx.strokeStyle = 'green';
        } else {
            ctx.strokeStyle = 'white';
        }

        ctx.lineWidth = 3;
        ctx.stroke();

    }, [feedbackStatus, visualFeedback, videoDimensions]);

    // UPDATED scaling logic: fix short side at 360 when larger
    const captureCleanFrame = useCallback(() => {
        if (!webcamRef.current || !hiddenCanvasRef.current) return null;

        const video = webcamRef.current.video;
        if (!video || !video.videoWidth || !video.videoHeight) return null;

        const canvas = hiddenCanvasRef.current;
        const ctx = canvas.getContext('2d');

        // Set canvas dimensions to match video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Draw the video frame to canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const originalWidth = canvas.width;
        const originalHeight = canvas.height;
        const shortSide = Math.min(originalWidth, originalHeight);
        const scale = shortSide > TARGET_SHORT_SIDE ? TARGET_SHORT_SIDE / shortSide : 1;
        lastScaleRef.current = scale;

        const scaledWidth = Math.round(originalWidth * scale);
        const scaledHeight = Math.round(originalHeight * scale);

        if (scale === 1) { // already small enough
            if (debugEnabled) {
                setSentFrameResolution({width: originalWidth, height: originalHeight, scale: 1});
            }
            return canvas.toDataURL('image/jpeg', 0.7);
        }

        // Create a temporary canvas for resizing
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = scaledWidth;
        tempCanvas.height = scaledHeight;
        const tempCtx = tempCanvas.getContext('2d');

        // Draw the original image onto the smaller canvas
        tempCtx.drawImage(canvas, 0, 0, scaledWidth, scaledHeight);

        if (debugEnabled) {
            // Only update if changed to avoid re-renders
            setSentFrameResolution(prev => {
                if (!prev || prev.width !== scaledWidth || prev.height !== scaledHeight) {
                    return {width: scaledWidth, height: scaledHeight, scale: parseFloat(scale.toFixed(3))};
                }
                return prev;
            });
        }

        // Return the resized image as data URL with reduced quality
        return tempCanvas.toDataURL('image/jpeg', 0.7);
    }, [webcamRef, debugEnabled]);

    const captureFrame = useCallback(() => {
        if (!isCapturing || !webcamRef.current) return;

        const video = webcamRef.current.video;
        if (!video || !video.videoWidth || !video.videoHeight) return;

        // Update video dimensions if changed (for overlay scaling)
        if (videoDimensions.width !== video.videoWidth || videoDimensions.height !== video.videoHeight) {
            setVideoDimensions({width: video.videoWidth, height: video.videoHeight});
        }

        // Throttle WebSocket requests to once per 200ms
        const currentTime = Date.now();
        if (isConnected && socketRef.current && (currentTime - lastSendTimeRef.current >= WS_SEND_INTERVAL_MS)) {
            // Get clean image without any drawings
            const imageData = captureCleanFrame();
            if (imageData) {
                socketRef.current.send(JSON.stringify({image: imageData}));
                lastSendTimeRef.current = currentTime;
            }
        }

        animationFrameRef.current = requestAnimationFrame(captureFrame);
    }, [isCapturing, isConnected, captureCleanFrame, videoDimensions]);

    const setupCanvas = useCallback(() => {
        if (!webcamRef.current || !canvasRef.current) return;
        const video = webcamRef.current.video;
        const canvas = canvasRef.current;
        if (!video) return;

        // Set intrinsic canvas resolution to match video frame
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Preserve intrinsic aspect ratio through CSS (width 100%, height auto)
        canvas.style.width = '100%';
        canvas.style.height = 'auto';

        // Setup hidden canvas for capturing clean images
        if (!hiddenCanvasRef.current) {
            const hiddenCanvas = document.createElement('canvas');
            hiddenCanvas.style.display = 'none';
            document.body.appendChild(hiddenCanvas);
            hiddenCanvasRef.current = hiddenCanvas;
        }

        setVideoDimensions({width: video.videoWidth, height: video.videoHeight});
        setVideoReady(true);
    }, [webcamRef]);

    // Cleanup function to remove all canvas elements and reset state
    const cleanupCanvasAndState = useCallback(() => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        if (canvasRef.current && videoContainerRef.current) {
            try {
                videoContainerRef.current.removeChild(canvasRef.current);
            } catch (err) {
                console.error('Error removing canvas:', err);
            }
            canvasRef.current = null;
        }

        if (hiddenCanvasRef.current) {
            try {
                document.body.removeChild(hiddenCanvasRef.current);
            } catch (err) {
                console.error('Error removing hidden canvas:', err);
            }
            hiddenCanvasRef.current = null;
        }

        setIsCapturing(false);
    }, []);

    const onUserMedia = useCallback(() => {
        if (!webcamRef.current || hide) return;
        console.log("Webcam ready");

        const video = webcamRef.current.video;
        if (!video) return;

        if (!videoContainerRef.current) {
            const container = document.getElementById('video-container');
            if (container) {
                videoContainerRef.current = container;
            } else {
                console.error('Video container element not found');
                return;
            }
        }

        if (!canvasRef.current && videoContainerRef.current) {
            try {
                const canvas = document.createElement('canvas');
                // Position canvas as overlay on top of video
                canvas.style.display = 'block';
                canvas.style.width = '100%';
                canvas.style.height = 'auto';
                canvas.style.position = 'absolute';
                canvas.style.top = '0';
                canvas.style.left = '0';
                canvas.style.zIndex = '1';
                canvas.style.pointerEvents = 'none'; // Allow clicks to pass through

                videoContainerRef.current.appendChild(canvas);
                canvasRef.current = canvas;

                if (video.readyState >= 2) {
                    setupCanvas();
                } else {
                    video.addEventListener('loadedmetadata', setupCanvas, {once: true});
                }

                connectWebSocket();

                setIsCapturing(true);
            } catch (err) {
                console.error('Error creating canvas:', err);
                // removed unnecessary return; allow function to exit naturally
            }
        }
    }, [webcamRef, hide, setupCanvas, connectWebSocket]);

    // Effect to handle facingMode changes
    useEffect(() => {
        // If facingMode changed, we need to reset our canvas and recreate everything
        if (facingModeRef.current !== facingMode) {
            console.log(`facingMode changed from ${facingModeRef.current} to ${facingMode}`);
            facingModeRef.current = facingMode;

            // Clean up existing canvas and state
            cleanupCanvasAndState();

            // The webcam will re-render with new facingMode, and onUserMedia will be called again
        }
    }, [facingMode, cleanupCanvasAndState]);

    useEffect(() => {
        if (isCapturing) {
            animationFrameRef.current = requestAnimationFrame(captureFrame);
        }

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [isCapturing, captureFrame]);

    useEffect(() => {
        return () => {
            if (socketRef.current) {
                socketRef.current.close();
            }

            cleanupCanvasAndState();
        };
    }, [cleanupCanvasAndState]);

    useEffect(() => {
        if (hide) {
            if (isCapturing) {
                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                }
                setIsCapturing(false);
            }
        } else {
            if (!isCapturing) {
                setIsCapturing(true);
                animationFrameRef.current = requestAnimationFrame(captureFrame);
            }
        }
    }, [hide, isCapturing, captureFrame]);

    const onAbort = useCallback(() => {
        console.log("Webcam aborted");
        cleanupCanvasAndState();
    }, [cleanupCanvasAndState]);

    // Redraw overlay only when feedback or dimensions change
    useEffect(() => {
        if (videoReady && videoDimensions.width > 0) {
            drawGuide();
        }
    }, [feedbackStatus, visualFeedback, videoDimensions, videoReady, drawGuide]);

    useEffect(() => {
        // Reinitialize canvas sizing on orientation changes
        const handler = () => {
            if (webcamRef.current?.video && canvasRef.current) {
                const video = webcamRef.current.video;
                canvasRef.current.width = video.videoWidth;
                canvasRef.current.height = video.videoHeight;
                setVideoDimensions({width: video.videoWidth, height: video.videoHeight});
            }
        };
        window.addEventListener('orientationchange', handler);
        window.addEventListener('resize', handler);
        return () => {
            window.removeEventListener('orientationchange', handler);
            window.removeEventListener('resize', handler);
        };
    }, [webcamRef]);

    // NEW: expose optimized clean capture function to parent for quick photo capture
    useEffect(() => {
        if (typeof onCapture === 'function') {
            onCapture(captureCleanFrame);
        }
    }, [onCapture, captureCleanFrame]);

    return (
        <CanvasWrapper>
            <div
                id="video-container"
                style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 720,
                    display: hide ? 'none' : 'block',
                    overflow: 'hidden',
                    borderRadius: 10
                }}
                className={mirror ? 'mirrorCanvas' : ''}
            >
                {!hide && (
                    // Show the real webcam feed
                    <Webcam
                        disabled={hide}
                        ref={webcamRef}
                        audio={false}
                        forceScreenshotSourceSize={true}
                        className="webcam"
                        screenshotFormat="image/jpeg"
                        screenshotQuality={0.9}
                        mirrored={mirror}
                        imageSmoothing={false}
                        style={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            position: 'relative',
                            zIndex: 0
                        }}
                        videoConstraints={{
                            facingMode,
                            width: {ideal: 2560, max: 3840},
                            height: {ideal: 1440, max: 2160}
                        }}
                        onUserMedia={onUserMedia}
                        onAbort={onAbort}
                    />
                )}
                {debugEnabled && sentFrameResolution && videoReady && (
                    <div style={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        background: 'rgba(0,0,0,0.55)',
                        color: '#0f0',
                        fontFamily: 'monospace',
                        fontSize: 12,
                        padding: '6px 8px',
                        borderRadius: 4,
                        lineHeight: 1.4,
                        zIndex: 99
                    }}>
                        <div>Sent {sentFrameResolution.width}x{sentFrameResolution.height}</div>
                        {sentFrameResolution.scale && <div>Scale {sentFrameResolution.scale}</div>}
                        <div>Short tgt 360px</div>
                        <div>Send Δ {WS_SEND_INTERVAL_MS}ms</div>
                        {lastServerMessageInterval != null && <div>Rx Δ {lastServerMessageInterval}ms</div>}
                        {isConnected ? <div style={{color: '#6cf'}}>WS: up</div> :
                            <div style={{color: '#f66'}}>WS: down</div>}
                        <div>Frozen: {isFrozenRef.current ? 'YES' : 'NO'}</div>
                    </div>
                )}
            </div>
            {statusMessages.length > 0 && !hide && (
                <div
                    id="status"
                    className="statusMessages"
                    style={{
                        height: 130,
                        minWidth: '300px',
                        padding: '15px',
                        border: '1px solid #ddd',
                        borderRadius: '5px',
                        backgroundColor: '#f9f9f9',
                        marginTop: '20px'
                    }}
                >
                    {statusMessages.map((msg, index) => (
                        <p
                            key={index}
                            style={{
                                color: msg.type === 'success' ? 'green' :
                                    msg.type === 'error' ? 'red' :
                                        msg.type === 'warning' ? 'orange' : 'black',
                                margin: '2px 0'
                            }}
                        >
                            {msg.text}
                        </p>
                    ))}
                </div>
            )}
        </CanvasWrapper>
    );
};

export default FaceDetection;

